import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import { api, errMsg, money } from "../../lib/api";

const TourPick = ({ t, on, toggle }) => (
  <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`lib-price-tour-${t.code}`}>
    <input type="checkbox" checked={on} onChange={toggle} data-testid={`lib-price-tour-check-${t.code}`} />
    <span className="font-semibold">{t.code}</span><span className="flex-1 truncate text-slate-600">{t.title}</span>
    <span className="text-xs text-slate-500">{t.start_date}</span><span className="text-xs">now {t.prices.map(money).join(" / ")}</span>
  </label>
);

export const LibraryPriceEditor = ({ tpl, onClose, onDone }) => {
  const [price, setPrice] = useState(String(tpl.content.price ?? 0));
  const [tours, setTours] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/staff/templates/${tpl.id}/usage`).then((r) => { setTours(r.data); setSel(r.data.map((t) => t.id)); }).catch((e) => toast.error(errMsg(e)));
  }, [tpl.id]);
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/templates/${tpl.id}/price`, { price: Number(price), tour_ids: sel });
      toast.success(`Library price set to ${money(data.price)}${data.updated_tours.length ? ` · updated ${data.updated_tours.join(", ")}` : " · no tours changed"}`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="lib-price-editor">
      <div className="flex items-center justify-between"><h4 className="font-semibold">Update price: {tpl.name}</h4>
        <button className="btn-ghost btn-sm" onClick={onClose} aria-label="Close" data-testid="lib-price-close"><X className="h-4 w-4" /></button></div>
      <div className="mt-3 max-w-xs"><label className="label" htmlFor="lib-price">New library price (USD)</label>
        <input id="lib-price" type="number" min="0" step="0.01" className="input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="lib-price-input" /></div>
      <p className="mt-4 text-sm font-medium">Also apply the new price to these upcoming tours:</p>
      <p className="text-xs text-slate-500">Existing bookings keep the price they registered at. Untick a tour to leave its price as it is.</p>
      <div className="mt-2 space-y-2">
        {tours === null && <p className="text-sm text-slate-500">Loading tours…</p>}
        {tours?.map((t) => <TourPick key={t.id} t={t} on={sel.includes(t.id)} toggle={() => toggle(t.id)} />)}
        {tours?.length === 0 && <p className="text-sm text-slate-500" data-testid="lib-price-no-tours">No upcoming tours use this add-on, so only the library price will change.</p>}
      </div>
      <button className="btn-primary mt-4" disabled={busy || price === "" || Number(price) < 0} onClick={save} data-testid="lib-price-save"><Check className="h-4 w-4" />Save price{sel.length ? ` & update ${sel.length} tour${sel.length > 1 ? "s" : ""}` : ""}</button>
    </div>
  );
};
